import { Theme } from "@nykaa/ui-components/themes/types";
import { VERNACULAR_DEFAULT_MESSAGES, VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { getStringFromId } from "helpers/vernacular";
import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import { handleBack } from "../../../layouts/CheckoutPayment/helpers";
import { TRACKING_EVENTS } from "constants/index";
import { allTrackingHandlerForApps } from "helpers/utils/trackingHandler";
import { trackAddressBackFrictioncontinueClick, trackAddressBackFrictionGoToCartClick } from "layouts/Payment/common/dependencies/tracking";

/**
 * @description -- Function to get config data for back friction bottomsheet or confirm modal
 */

export const getBackFrictionData = (
  theme: Theme,
  totalSaved: number,
  setBottomSheetOrPopup: (value: boolean) => void,
  trackContinuewithPaymentClick: () => void,
  trackCancelPaymentClick: () => void,
  platform: string,
  isExperimentEligible: boolean,
  isApp: boolean,
) => {
  /**
   * @description -- function called on Continue with Payment button Click
   */
  const handleContinuePaymentButtonClick = () => {
    trackContinuewithPaymentClick();
    allTrackingHandlerForApps({ event: TRACKING_EVENTS.PAYMENT_BACK_FRICTION_PROCEED, isApp });
    setBottomSheetOrPopup(false);
    window?.history?.pushState({ prevUrl: window.location?.href }, "", window?.location?.href);
  };

  /**
   * @description -- function called on Cancel Payment button Click
   */
  const handleCancelPaymentButtonClick = () => {
    trackCancelPaymentClick();
    allTrackingHandlerForApps({ event: TRACKING_EVENTS.PAYMENT_BACK_FRICTION_CLOSED, isApp });
    setBottomSheetOrPopup(false);
    handleBack(isApp);
  };
  /**
   * check whether backfriction  will be visible to user or not
   */
  const successButtonText = getStringFromId(VERNACULAR_STRING_IDS.GO_BACK_TO_CART, VERNACULAR_DEFAULT_MESSAGES.GO_BACK_TO_CART);
  const successButtonTextForDesktop = getStringFromId(VERNACULAR_STRING_IDS.GO_BACK, VERNACULAR_DEFAULT_MESSAGES.GO_BACK);
  const cancelButtonText = getStringFromId(VERNACULAR_STRING_IDS.CONTINUE_WITH_PAYMENT, VERNACULAR_DEFAULT_MESSAGES.CONTINUE_WITH_PAYMENT);
  const textMessage = getStringFromId(VERNACULAR_STRING_IDS.BACK_FRICTION_MESSAGE, `Don't miss out on the <span style =font-weight:500;color:${theme?.colors?.positive}>savings of ₹${totalSaved}</span> that you have availed on your order today!`, { totalSaved: totalSaved });
  /**
   * mobile and desktop backfriction(bottomsheet or modal) common confirguartion
   */
  let commonConfig = {
    leadingIcon: getIcons(platform, ICONS_NAMES.WALLET_DISCOUNT_ALERT),
    title: getStringFromId(VERNACULAR_STRING_IDS.YOUR_ORDER_IS_ALMOST_DONE, VERNACULAR_DEFAULT_MESSAGES.YOUR_ORDER_IS_ALMOST_DONE),
    htmlSubTitle: totalSaved ? textMessage : "",
    theme,
  };

  /**
   * mobile bottomsheet backfriction confirguration
   */
  let mobileConfig = {
    ...commonConfig,
    isVisible: !!(isExperimentEligible),
    titleFont: theme?.typography?.titleMedium,
    successButtonText: successButtonText,
    onSuccess: handleCancelPaymentButtonClick,
    dismissButtonText: cancelButtonText,
    onDismiss: handleContinuePaymentButtonClick,
    onClose: () => {},
    noContentPadding: false, // bottomsheet body(icon, title, subTitle) has padding
    titleCentered: false,
    noInnnerContentPadding: true, // bottomsheet inner content does not have pading
  };

  /**
   * desktop modal backfriction configuartion
   */
  let desktopConfig = {
    ...commonConfig,
    isModalShow: !!(isExperimentEligible),
    titleFont: theme?.typography?.titleMedium,
    subTitleFont: theme?.typography?.bodyLarge,
    primaryButtonText: successButtonTextForDesktop,
    primaryButtonAction: handleCancelPaymentButtonClick,
    secondaryButtonText: cancelButtonText,
    secondaryButtonAction: handleContinuePaymentButtonClick,
    leadingIconCentered: false,
  };

  /**
   * Below config is for addresspage backfriction bottomsheet.
   * This is only for Mweb;
   */
  const addressBackFrictionMessage = totalSaved
    ? getStringFromId(
        VERNACULAR_STRING_IDS.ADDRESS_BACKFRICTION_MESSAGE,
        `You're nearly done! Save your address and place your order now to avail your discount of <span style =font-weight:500;color:${theme?.colors?.positive}>₹${totalSaved}</span>.`,
        { totalSaved: totalSaved }
      )
    : getStringFromId(
        VERNACULAR_STRING_IDS.ADDRESS_BACKFRICTION_MESSAGE,
        VERNACULAR_DEFAULT_MESSAGES.ADDRESS_BACKFRICTION_MESSAGE
      );

  let addressPageConfig = {
    ...commonConfig,
    title: getStringFromId(
      VERNACULAR_STRING_IDS.ADDRESS_BACKFRICTION_TITLE,
      VERNACULAR_DEFAULT_MESSAGES.ADDRESS_BACKFRICTION_TITLE
    ),
    htmlSubTitle: addressBackFrictionMessage,
    isVisible: !!isExperimentEligible,
    titleFont: theme?.typography?.titleMedium,
    successButtonText: getStringFromId(
      VERNACULAR_STRING_IDS.ADDRESS_BACKFRICTION_PRIMARY_BUTTON_TXT,
      VERNACULAR_DEFAULT_MESSAGES.ADDRESS_BACKFRICTION_PRIMARY_BUTTON_TXT
    ),
    dismissButtonText: getStringFromId(
      VERNACULAR_STRING_IDS.ADDRESS_BACKFRICTION_SECONDARY_BUTTON_TXT,
      VERNACULAR_DEFAULT_MESSAGES.ADDRESS_BACKFRICTION_SECONDARY_BUTTON_TXT
    ),
    leadingIcon: getIcons(platform, ICONS_NAMES.ADDRESS_BACKFRICTION_LEADING_ICON),
    onSuccess: () => {
      /**
       * tracking events
       */
       trackAddressBackFrictioncontinueClick();
       allTrackingHandlerForApps({
         event: TRACKING_EVENTS.ADDRESS_BACK_BOTTOMSHEET_CLICK,
         interactionLocation: "continue",
         isApp,
       });
      setBottomSheetOrPopup(false);
    },
    onDismiss: () => {
      /**
       * tracking events
       */
      trackAddressBackFrictionGoToCartClick();
      allTrackingHandlerForApps({
        event: TRACKING_EVENTS.ADDRESS_BACK_BOTTOMSHEET_CLICK,
        interactionLocation: "goToCart",
        isApp,
      });
      /**
       * adding extra window.history.back() because we are adding extra url i.e #disableBack
       * for opning bottom sheet on address page on Mweb.
       */
      window.history.back();
      handleBack(isApp);
    },
    onClose: () => {
      setBottomSheetOrPopup(false);
    },
    noContentPadding: false, // bottomsheet body(icon, title, subTitle) has padding
    titleCentered: false,
    noInnnerContentPadding: true, // bottomsheet inner content does not have pading
  };

  return { mobileConfig, desktopConfig, addressPageConfig };
};
